import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/billing/twikey';

const CreateTwikeyMandateVariablesSchema = z.object({
  locale: z.string().min(1),
});

export type CreateTwikeyMandateVariables = z.infer<typeof CreateTwikeyMandateVariablesSchema>;

// The backend returns a short-lived URL the user must open to sign the SEPA
// mandate on Twikey's hosted page. The mandate itself lands out-of-band via
// webhook, so there is nothing to optimistically refetch here.
export const TwikeyMandateResponseSchema = z.object({
  twikeyUrl: z.string(),
});

export type TwikeyMandateResponse = AxiosResponse<z.infer<typeof TwikeyMandateResponseSchema>>;

export async function createTwikeyMandateFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateTwikeyMandateVariables,
) {
  const instance = await axiosInstance;

  return instance.post(URL, CreateTwikeyMandateVariablesSchema.parse(variables));
}

export function useCreateTwikeyMandate() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<TwikeyMandateResponse, ApiError, CreateTwikeyMandateVariables>({
    mutationFn: variables => createTwikeyMandateFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['paymentConfiguration'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    TwikeyMandateResponse,
    ApiError,
    CreateTwikeyMandateVariables,
    'createTwikeyMandate'
  >('createTwikeyMandate', mutation);
}
